/**
 * Maya admin dashboard.
 *
 *  - Revenue summary (total, pass, destination unlocks).
 *  - Conversion funnel.
 *  - Top destinations / topics / languages.
 *  - Purchase explorer with email/destination search.
 *
 * Gated server-side: getMayaDashboard throws "Forbidden" unless the caller
 * has `admin` or `owner` role.
 */
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldAlert, Sparkles, Trash2, Search, Mail, Crown, Send } from "lucide-react";
import { getMayaDashboard, getMayaSalesFunnel, listMayaPurchasesAdmin } from "@/lib/maya-admin.functions";
import {
  createMayaGrant,
  getMayaOwnerMetrics,
  listMayaGrants,
  resendMayaGrantEmail,
  revokeMayaGrant,
  searchMayaUsers,
  type MayaGrantRow,
  type MayaUserSearchRow,
} from "@/lib/maya-access.functions";


export const Route = createFileRoute("/_authenticated/admin/maya")({
  head: () => ({
    meta: [
      { title: "Maya · Panel de administración" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MayaAdminPage,
});

function fmtMoney(cents: number, currency = "usd"): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format((cents ?? 0) / 100);
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

function MayaAdminPage() {
  const fetchDashboard = useServerFn(getMayaDashboard);
  const fetchPurchases = useServerFn(listMayaPurchasesAdmin);
  const [search, setSearch] = useState("");

  const dashboard = useQuery({
    queryKey: ["maya-admin-dashboard"],
    queryFn: () => fetchDashboard(),
    retry: false,
  });

  const purchases = useQuery({
    queryKey: ["maya-admin-purchases", search],
    queryFn: () => fetchPurchases({ data: { search: search || undefined, limit: 100 } }),
    retry: false,
  });

  if (dashboard.isLoading) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (dashboard.error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
        <h1 className="mt-4 font-display text-2xl font-semibold">Acceso restringido</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Esta sección es solo para administradores de Mostrando México.
        </p>
      </div>
    );
  }

  const d = dashboard.data!;
  const checkoutStarted = d.funnel.find((f: { event_type: string; count: number }) => f.event_type === "checkout_started")?.count ?? 0;
  const purchasesCompleted = d.funnel.find((f: { event_type: string; count: number }) => f.event_type === "purchase_completed")?.count ?? 0;
  const conversionRate = checkoutStarted > 0 ? (purchasesCompleted / checkoutStarted) * 100 : 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Maya Premium</p>
        <h1 className="mt-1 font-display text-3xl font-semibold">Panel de administración</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Ingresos, compras y métricas de la guía virtual Maya.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Ingresos totales" value={fmtMoney(d.revenue.total_revenue_cents)} />
        <StatCard
          label="Maya Pass México"
          value={fmtMoney(d.revenue.pass_revenue_cents)}
          hint={`${d.revenue.pass_count} pases`}
        />
        <StatCard
          label="Desbloqueos por destino"
          value={fmtMoney(d.revenue.destination_revenue_cents)}
          hint={`${d.revenue.destination_count} compras`}
        />
        <StatCard
          label="Conversión paywall → compra"
          value={`${conversionRate.toFixed(1)}%`}
          hint={`${purchasesCompleted} / ${checkoutStarted}`}
        />
      </section>

      <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Conversaciones" value={d.totals.conversations.toLocaleString("es-MX")} />
        <StatCard label="Mensajes" value={d.totals.messages.toLocaleString("es-MX")} />
        <StatCard label="Eventos registrados" value={d.totals.events.toLocaleString("es-MX")} />
      </section>

      <MayaSalesFunnel />

      <OwnerMetrics />


      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="🌍 Destinos más populares">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="pb-2">Destino</th>
                <th className="pb-2 text-right">Vistas</th>
                <th className="pb-2 text-right">Compras</th>
                <th className="pb-2 text-right">Ingresos</th>
              </tr>
            </thead>
            <tbody>
              {d.topDestinations.length === 0 && <EmptyRow cols={4} />}
              {d.topDestinations.map((row: { destination: string; requests: number; purchases: number; revenue_cents: number }) => (
                <tr key={row.destination} className="border-t border-border/60">
                  <td className="py-2 font-medium">{row.destination}</td>
                  <td className="py-2 text-right">{row.requests}</td>
                  <td className="py-2 text-right">{row.purchases}</td>
                  <td className="py-2 text-right">{fmtMoney(Number(row.revenue_cents))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <Panel title="📚 Tópicos más solicitados">
          <ul className="space-y-2 text-sm">
            {d.topTopics.length === 0 && <li className="text-muted-foreground">Sin datos aún.</li>}
            {d.topTopics.map((t: { topic: string; requests: number }) => (
              <li key={t.topic} className="flex items-center justify-between">
                <span className="font-medium">{t.topic}</span>
                <span className="text-muted-foreground">{t.requests}</span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="🌐 Idiomas">
          <ul className="space-y-2 text-sm">
            {d.topLanguages.length === 0 && <li className="text-muted-foreground">Sin datos aún.</li>}
            {d.topLanguages.map((l: { lang: string; conversations: number; revenue_cents: number }) => (
              <li key={l.lang} className="flex items-center justify-between">
                <span className="font-medium uppercase">{l.lang}</span>
                <span className="text-muted-foreground">
                  {l.conversations} chats · {fmtMoney(Number(l.revenue_cents))}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </section>

      <section className="mt-8">
        <Panel
          title="💳 Compras"
          actions={
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por email, destino o producto…"
              className="h-9 w-72 rounded-full border border-border bg-background px-4 text-sm"
            />
          }
        >
          {purchases.isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    <th className="pb-2">Fecha</th>
                    <th className="pb-2">Email</th>
                    <th className="pb-2">Producto</th>
                    <th className="pb-2">Destino</th>
                    <th className="pb-2 text-right">Monto</th>
                    <th className="pb-2">Estado</th>
                    <th className="pb-2">Cuenta</th>
                  </tr>
                </thead>
                <tbody>
                  {(purchases.data ?? []).length === 0 && <EmptyRow cols={7} />}
                  {(purchases.data ?? []).map((p: import("@/lib/maya-admin.functions").MayaPurchaseRow) => (
                    <tr key={p.id} className="border-t border-border/60 align-top">
                      <td className="py-2 text-xs text-muted-foreground">{fmtDate(p.created_at)}</td>
                      <td className="py-2">{p.customer_email}</td>
                      <td className="py-2">
                        {p.product_id === "maya_pass_one_time" ? "🌺 Maya Pass" : "🎧 Destino"}
                      </td>
                      <td className="py-2 text-xs">{p.destination_key ?? "—"}</td>
                      <td className="py-2 text-right">{p.amount != null ? fmtMoney(p.amount, p.currency ?? "usd") : "—"}</td>
                      <td className="py-2">
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                          {p.status ?? "paid"}
                        </span>
                      </td>
                      <td className="py-2 text-xs">
                        {p.user_id ? <span className="text-emerald-600">vinculada</span> : <span className="text-amber-600">por reclamar</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </section>

      <section className="mt-10">
        <MayaAccessManager />
      </section>

      <section className="mt-8">
        <MayaUserSearch />
      </section>
    </div>
  );
}

// ─── Owner metrics strip ───
function OwnerMetrics() {
  const fetchMetrics = useServerFn(getMayaOwnerMetrics);
  const q = useQuery({
    queryKey: ["maya-owner-metrics"],
    queryFn: () => fetchMetrics(),
    retry: false,
  });
  if (q.isLoading || !q.data) return null;
  const m = q.data;
  return (
    <section className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <StatCard label="Usuarios Maya totales" value={m.totalUsers.toLocaleString("es-MX")} />
      <StatCard label="Usuarios Premium (pago)" value={m.premiumUsers.toLocaleString("es-MX")} />
      <StatCard label="Maya Pass" value={m.passUsers.toLocaleString("es-MX")} />
      <StatCard label="Acceso manual" value={m.manualAccessUsers.toLocaleString("es-MX")} />
      <StatCard label="Oyentes activos (30d)" value={m.activeListeners.toLocaleString("es-MX")} />
    </section>
  );
}

// ─── 🌺 Maya Access Manager (grant form + grants list) ───
const ACCESS_TYPE_OPTIONS = [
  { value: "destination", label: "Acceso a un destino" },
  { value: "multi_destination", label: "Acceso a varios destinos" },
  { value: "unlimited", label: "Acceso ilimitado" },
  { value: "pass", label: "Maya Pass México" },
  { value: "lifetime", label: "Acceso de por vida" },
] as const;

const EXPIRY_OPTIONS = [
  { value: "24h", label: "24 horas" },
  { value: "7d", label: "7 días" },
  { value: "30d", label: "30 días" },
  { value: "1y", label: "1 año" },
  { value: "never", label: "Nunca expira" },
] as const;

const DESTINATION_SUGGESTIONS = [
  "Chichén Itzá",
  "Tulum",
  "Holbox",
  "Bacalar",
  "Cozumel",
  "Akumal",
  "Isla Mujeres",
  "Playa del Carmen",
];

type AccessType = (typeof ACCESS_TYPE_OPTIONS)[number]["value"];
type ExpiryPreset = (typeof EXPIRY_OPTIONS)[number]["value"];

function MayaAccessManager() {
  const qc = useQueryClient();
  const fetchGrants = useServerFn(listMayaGrants);
  const grantFn = useServerFn(createMayaGrant);
  const revokeFn = useServerFn(revokeMayaGrant);
  const resendFn = useServerFn(resendMayaGrantEmail);


  const [email, setEmail] = useState("");
  const [accessType, setAccessType] = useState<AccessType>("destination");
  const [destinationInput, setDestinationInput] = useState("");
  const [destinations, setDestinations] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState<ExpiryPreset>("never");
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [status, setStatus] = useState<"all" | "active" | "expired" | "revoked">("active");
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  // Per-tour invite fields — power the tokenized "Regalar guía" link flow.
  const [tourSlug, setTourSlug] = useState("");
  const [tourTitle, setTourTitle] = useState("");
  const [bokunProductId, setBokunProductId] = useState("");
  const [language, setLanguage] = useState<string>("es");

  const grantsQ = useQuery({
    queryKey: ["maya-grants", status],
    queryFn: () => fetchGrants({ data: { status, limit: 100 } }),
    retry: false,
  });

  const needsDestinations =
    accessType === "destination" || accessType === "multi_destination";

  const addDestination = (d: string) => {
    const v = d.trim();
    if (!v) return;
    if (destinations.includes(v)) return;
    setDestinations([...destinations, v]);
    setDestinationInput("");
  };

  const createMut = useMutation({
    mutationFn: async () => {
      const res = await grantFn({
        data: {
          email: email.trim(),
          accessType,
          destinationKeys: needsDestinations ? destinations : [],
          expiresAt,
          note: note.trim() || undefined,
          sendEmail,
          tourSlug: tourSlug.trim() || undefined,
          tourTitle: tourTitle.trim() || undefined,
          bokunProductId: bokunProductId.trim() || undefined,
          language: language.trim() || undefined,
        },
      });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: (res) => {
      if (sendEmail && !("error" in res) && res.emailSent === false) {
        setFeedback({
          kind: "err",
          msg: `Acceso creado, pero el correo NO se envió: ${res.emailReason ?? "razón desconocida"}. Revisa Cloud → Emails.`,
        });
      } else if (sendEmail) {
        setFeedback({ kind: "ok", msg: "Acceso otorgado y correo encolado correctamente." });
      } else {
        setFeedback({ kind: "ok", msg: "Acceso otorgado correctamente." });
      }
      setEmail("");
      setDestinations([]);
      setNote("");
      setTourSlug("");
      setTourTitle("");
      setBokunProductId("");
      qc.invalidateQueries({ queryKey: ["maya-grants"] });
      qc.invalidateQueries({ queryKey: ["maya-owner-metrics"] });
      qc.invalidateQueries({ queryKey: ["maya-access"] });
    },
    onError: (err: Error) => setFeedback({ kind: "err", msg: err.message }),
  });

  const revokeMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await revokeFn({ data: { id } });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maya-grants"] });
      qc.invalidateQueries({ queryKey: ["maya-owner-metrics"] });
      qc.invalidateQueries({ queryKey: ["maya-access"] });
    },
  });

  const resendMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await resendFn({ data: { id } });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: (res) => {
      if ("error" in res) return;
      if (res.emailSent) {
        setFeedback({ kind: "ok", msg: "Correo de invitación reenviado correctamente." });
      } else {
        setFeedback({
          kind: "err",
          msg: `No se pudo reenviar el correo: ${res.emailReason ?? "razón desconocida"}. Revisa Cloud → Emails.`,
        });
      }
      qc.invalidateQueries({ queryKey: ["maya-grants"] });
    },
    onError: (err: Error) => setFeedback({ kind: "err", msg: err.message }),
  });


  return (
    <Panel
      title="🌺 Maya Access Manager"
      actions={
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className="h-9 rounded-full border border-border bg-background px-3 text-sm"
        >
          <option value="all">Todas</option>
          <option value="active">Activas</option>
          <option value="expired">Expiradas</option>
          <option value="revoked">Revocadas</option>
        </select>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        {/* Grant form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFeedback(null);
            createMut.mutate();
          }}
          className="space-y-3 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4"
        >
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Otorgar acceso cortesía
          </div>
          <label className="block text-xs font-medium">
            Email del usuario
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@ejemplo.com"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-xs font-medium">
            Tipo de acceso
            <select
              value={accessType}
              onChange={(e) => setAccessType(e.target.value as AccessType)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {ACCESS_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          {needsDestinations && (
            <div>
              <label className="block text-xs font-medium">Destinos</label>
              <div className="mt-1 flex gap-2">
                <input
                  list="maya-dest-suggestions"
                  value={destinationInput}
                  onChange={(e) => setDestinationInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addDestination(destinationInput);
                    }
                  }}
                  placeholder="Ej. Chichén Itzá"
                  className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => addDestination(destinationInput)}
                  className="rounded-md border border-border px-3 text-sm hover:bg-accent"
                >
                  +
                </button>
              </div>
              <datalist id="maya-dest-suggestions">
                {DESTINATION_SUGGESTIONS.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              {destinations.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {destinations.map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5 text-xs"
                    >
                      {d}
                      <button
                        type="button"
                        onClick={() => setDestinations(destinations.filter((x) => x !== d))}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Optional tour targeting — turns the grant into a tokenized invite link. */}
          <fieldset className="rounded-md border border-border/60 bg-background/60 p-3">
            <legend className="px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Regalar guía de un tour (opcional)
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="block text-xs font-medium">
                Tour slug
                <input
                  value={tourSlug}
                  onChange={(e) => setTourSlug(e.target.value)}
                  placeholder="isla-mujeres-premium"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs font-medium">
                Título del tour
                <input
                  value={tourTitle}
                  onChange={(e) => setTourTitle(e.target.value)}
                  placeholder="Isla Mujeres Premium Catamarán"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs font-medium">
                Bokun product ID
                <input
                  value={bokunProductId}
                  onChange={(e) => setBokunProductId(e.target.value)}
                  placeholder="opcional"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs font-medium">
                Idioma
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="pt">Português</option>
                  <option value="it">Italiano</option>
                </select>
              </label>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Si indicas un tour, el correo llevará un enlace único que abre directamente la Guía IA.
            </p>
          </fieldset>
          <label className="block text-xs font-medium">

            Expiración
            <select
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value as ExpiryPreset)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-medium">
            Nota interna (opcional)
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Influencer, prensa, VIP, cliente…"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <Mail className="h-3.5 w-3.5" />
            Enviar invitación por email
          </label>
          {feedback && (
            <p
              className={
                feedback.kind === "ok"
                  ? "rounded-md border border-emerald-400/40 bg-emerald-50 p-2 text-xs text-emerald-700"
                  : "rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive"
              }
            >
              {feedback.msg}
            </p>
          )}
          <button
            type="submit"
            disabled={createMut.isPending}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Otorgar acceso
          </button>
        </form>

        {/* Grants list */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="pb-2">Email</th>
                <th className="pb-2">Tipo</th>
                <th className="pb-2">Destinos</th>
                <th className="pb-2">Expira</th>
                <th className="pb-2">Otorgado por</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {grantsQ.isLoading && <EmptyRow cols={6} label="Cargando…" />}
              {!grantsQ.isLoading && (grantsQ.data ?? []).length === 0 && <EmptyRow cols={6} />}
              {(grantsQ.data ?? []).map((g: MayaGrantRow) => (
                <tr key={g.id} className="border-t border-border/60 align-top">
                  <td className="py-2">{g.email}</td>
                  <td className="py-2 text-xs">
                    {ACCESS_TYPE_OPTIONS.find((o) => o.value === g.access_type)?.label ?? g.access_type}
                  </td>
                  <td className="py-2 text-xs">
                    {g.destination_keys.length ? g.destination_keys.join(", ") : "—"}
                  </td>
                  <td className="py-2 text-xs">
                    {g.expires_at ? fmtDate(g.expires_at) : "Nunca"}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">
                    {g.granted_by_email ?? "—"}
                  </td>
                  <td className="py-2 text-right">
                    {g.revoked_at ? (
                      <span className="text-xs text-muted-foreground">revocado</span>
                    ) : (
                      <div className="inline-flex flex-wrap items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setFeedback(null);
                            resendMut.mutate(g.id);
                          }}
                          disabled={resendMut.isPending}
                          title={g.email_sent_at ? `Último envío: ${fmtDate(g.email_sent_at)}` : "Aún no enviado"}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-primary/10 hover:text-primary disabled:opacity-60"
                        >
                          {resendMut.isPending && resendMut.variables === g.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          {g.email_sent_at ? "Reenviar" : "Enviar correo"}
                        </button>
                        <button
                          onClick={() => revokeMut.mutate(g.id)}
                          disabled={revokeMut.isPending}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" /> Revocar
                        </button>
                      </div>
                    )}
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}

// ─── 🔎 User search ───
function MayaUserSearch() {
  const fetchSearch = useServerFn(searchMayaUsers);
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");

  const q = useQuery({
    queryKey: ["maya-user-search", submitted],
    queryFn: () => fetchSearch({ data: { query: submitted } }),
    enabled: submitted.length >= 2,
    retry: false,
  });

  return (
    <Panel
      title="🔎 Búsqueda de usuarios Maya"
      actions={
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(query.trim());
          }}
          className="flex gap-2"
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Email, nombre o ID de usuario"
            className="h-9 w-72 rounded-full border border-border bg-background px-4 text-sm"
          />
          <button
            type="submit"
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 text-sm font-medium text-primary-foreground"
          >
            <Search className="h-3.5 w-3.5" /> Buscar
          </button>
        </form>
      }
    >
      {!submitted && (
        <p className="py-4 text-sm text-muted-foreground">
          Ingresa un email, nombre o ID para ver acceso, destinos desbloqueados y historial.
        </p>
      )}
      {submitted && q.isLoading && (
        <p className="py-4 text-sm text-muted-foreground">Buscando…</p>
      )}
      {submitted && !q.isLoading && (q.data ?? []).length === 0 && (
        <p className="py-4 text-sm text-muted-foreground">Sin resultados.</p>
      )}
      {(q.data ?? []).map((u: MayaUserSearchRow) => (
        <div key={u.email} className="mt-3 rounded-xl border border-border bg-background p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {u.isOwner && <Crown className="h-4 w-4 text-amber-500" />}
              <span className="font-semibold">{u.email}</span>
              {u.userId ? (
                <span className="text-xs text-muted-foreground">{u.userId.slice(0, 8)}…</span>
              ) : (
                <span className="text-xs text-amber-600">sin cuenta</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 text-xs">
              {u.isOwner && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">Owner — acceso total</span>
              )}
              {u.hasPass && (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">Maya Pass</span>
              )}
              <span className="rounded-full bg-muted px-2 py-0.5">
                {u.destinations.length} destino{u.destinations.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          {u.destinations.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              <strong className="text-foreground">Desbloqueados:</strong>{" "}
              {u.destinations.join(", ")}
            </p>
          )}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Concesiones</h4>
              {u.grants.length === 0 ? (
                <p className="mt-1 text-xs text-muted-foreground">Ninguna.</p>
              ) : (
                <ul className="mt-1 space-y-1 text-xs">
                  {u.grants.map((g) => (
                    <li key={g.id}>
                      {g.accessType} · {g.destinationKeys.join(", ") || "—"} ·{" "}
                      {g.expiresAt ? fmtDate(g.expiresAt) : "nunca"}
                      {g.revokedAt && <span className="ml-1 text-destructive">(revocado)</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Compras</h4>
              {u.purchases.length === 0 ? (
                <p className="mt-1 text-xs text-muted-foreground">Ninguna.</p>
              ) : (
                <ul className="mt-1 space-y-1 text-xs">
                  {u.purchases.map((p) => (
                    <li key={p.id}>
                      {p.productId === "maya_pass_one_time" ? "Maya Pass" : "Destino"}
                      {p.destinationKey ? ` · ${p.destinationKey}` : ""} · {fmtDate(p.createdAt)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ))}
    </Panel>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}



function Panel({ title, actions, children }: { title: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        {actions}
      </header>
      {children}
    </div>
  );
}

function EmptyRow({ cols, label }: { cols: number; label?: string }) {
  return (
    <tr>
      <td colSpan={cols} className="py-6 text-center text-sm text-muted-foreground">
        {label ?? "Sin datos todavía."}
      </td>
    </tr>
  );
}


function MayaSalesFunnel() {
  const fetchFunnel = useServerFn(getMayaSalesFunnel);
  const q = useQuery({ queryKey: ["maya-sales-funnel"], queryFn: () => fetchFunnel() });
  const rows = q.data ?? [];
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section className="mt-6 rounded-2xl border bg-card p-5">
      <h2 className="font-display text-lg font-semibold">Embudo de ventas de Maya</h2>
      <p className="text-xs text-muted-foreground">Desde la conversación hasta la reserva confirmada (sin datos personales).</p>
      {q.isLoading ? (
        <Loader2 className="mt-4 h-5 w-5 animate-spin text-primary" />
      ) : (
        <ul className="mt-4 space-y-2">
          {rows.map((r) => (
            <li key={r.key} className="grid grid-cols-[180px_1fr_60px] items-center gap-3 text-sm">
              <span className="text-muted-foreground">{r.label}</span>
              <span className="h-2 rounded-full bg-muted">
                <span className="block h-2 rounded-full bg-primary" style={{ width: `${(r.count / max) * 100}%` }} />
              </span>
              <span className="text-right font-semibold tabular-nums">{r.count.toLocaleString("es-MX")}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
